import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Cpu, Layers, Palette, Leaf, Download, ArrowRight, Globe, Wrench, Factory, Droplets } from "lucide-react";
import { SiFacebook, SiLinkedin, SiYoutube, SiInstagram } from "react-icons/si";
import { ROUTE_PATHS } from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface LayoutEsProps {
  children: React.ReactNode;
}

export function LayoutEs({
  children
}: LayoutEsProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productionItemsEs = [{
    title: "Fundición",
    href: `${ROUTE_PATHS.PRODUCTION_ES}#fundicion`,
    icon: <Cpu className="h-5 w-5" />,
    desc: "Producción de lingotes de aluminio de alta calidad."
  }, {
    title: "Fabricación de Moldes",
    href: `${ROUTE_PATHS.PRODUCTION_ES}#fabricacion-moldes`,
    icon: <Layers className="h-5 w-5" />,
    desc: "Servicios de diseño y fabricación de moldes de extrusión."
  }, {
    title: "Extrusión",
    href: `${ROUTE_PATHS.PRODUCTION_ES}#extrusion`,
    icon: <Cpu className="h-5 w-5" />,
    desc: "Capacidad de línea de extrusión de 4\" - 8\" de ancho."
  }, {
    title: "Tratamiento Superficial",
    href: `${ROUTE_PATHS.PRODUCTION_ES}#tratamiento-superficial`,
    icon: <Palette className="h-5 w-5" />,
    desc: "Instalaciones de anodizado y recubrimiento en polvo electrostático."
  }, {
    title: "Procesamiento Mecánico",
    href: `${ROUTE_PATHS.PRODUCTION_ES}#mecanico`,
    icon: <Layers className="h-5 w-5" />,
    desc: "Soluciones de corte, perforación y mecanizado CNC de precisión."
  }];

  const productItemsEs = [{
    title: "Sistemas Arquitectónicos",
    href: `${ROUTE_PATHS.PRODUCTS_ES}#sistemas-arquitectonicos`,
    desc: "Soluciones estéticas para sistemas de ventanas, puertas y fachadas."
  }, {
    title: "Perfiles Industriales",
    href: `${ROUTE_PATHS.PRODUCTS_ES}#perfiles-industriales`,
    desc: "Perfiles de aleación especial para sectores automotriz, energético y maquinaria."
  }, {
    title: "Sistemas de Fachada",
    href: ROUTE_PATHS.FACADE_SYSTEMS_ES,
    desc: "Soluciones estéticas de revestimiento de fachadas y carpintería para edificios modernos."
  }];

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top Bar */}
      <div className="hidden lg:block py-2 text-primary-foreground bg-[rgb(29,46,105)]">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs">
          <div className="flex gap-6">
            <span className="flex items-center gap-1.5 font-bold text-[rgb(255,255,255)]">
              <Phone className="h-3.5 w-3.5" /> +90 212 555 0000
            </span>
            <span className="flex items-center gap-1.5 font-bold text-[rgb(255,255,255)]">
              <Mail className="h-3.5 w-3.5" /> info@mimalum.com.tr
            </span>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Link to="/" className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)] hover:opacity-100 opacity-70 transition-opacity">
                TR
              </Link>
              <span className="opacity-50 text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">|</span>
              <Link to="/en" className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)] hover:opacity-100 opacity-70 transition-opacity">
                EN
              </Link>
              <span className="opacity-50 text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">|</span>
              <span className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">ES</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b bg-white", isScrolled ? "shadow-md" : "")}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to={ROUTE_PATHS.HOME_ES} className="flex items-center -ml-4">
            <img src={IMAGES.LOGO_276} alt="Company Logo" className="h-[100px] object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.HOME_ES}>Inicio</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.COMPANY_ES}>Empresa</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Productos</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 p-6 w-[400px]">
                      {productItemsEs.map(item => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <Link to={item.href} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                              <div className="text-sm font-semibold leading-none">{item.title}</div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                                {item.desc}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Producción</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {productionItemsEs.map(item => (
                        <li key={item.title}>
                          <NavigationMenuLink asChild>
                            <Link to={item.href} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                              <div className="flex items-center gap-2">
                                {item.icon}
                                <div className="text-sm font-medium leading-none">{item.title}</div>
                              </div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-2">
                                {item.desc}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.SUSTAINABILITY_ES}>Sostenibilidad</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.PROFILE_GUIDE_ES} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Guía de Perfiles</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.CONTACT_ES}>Contacto</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Quote Button */}
          <div className="hidden lg:block">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2 font-semibold">
              <Link to={`${ROUTE_PATHS.CONTACT_ES}#quote-form`}>Solicitar Cotización</Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto max-h-screen">
                <SheetHeader>
                  <SheetTitle>Navegación</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  <NavLink to={ROUTE_PATHS.HOME_ES} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Inicio</NavLink>
                  <NavLink to={ROUTE_PATHS.COMPANY_ES} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Empresa</NavLink>
                  
                  <div className="space-y-2">
                    <div className="text-lg font-medium">Productos</div>
                    <div className="pl-4 space-y-2">
                      {productItemsEs.map(item => (
                        <Link key={item.href} to={item.href} className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground uppercase tracking-widest font-bold">Producción</span>
                    {productionItemsEs.map(item => (
                      <Link key={item.title} to={item.href} className="pl-4 text-md py-1" onClick={() => setIsSheetOpen(false)}>{item.title}</Link>
                    ))}
                  </div>

                  <NavLink to={ROUTE_PATHS.SUSTAINABILITY_ES} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Sostenibilidad</NavLink>
                  <NavLink to={ROUTE_PATHS.PROFILE_GUIDE_ES} className="text-lg font-medium" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setIsSheetOpen(false); }}>Guía de Perfiles</NavLink>
                  <NavLink to={ROUTE_PATHS.CONTACT_ES} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Contacto</NavLink>

                  <div className="pt-4 border-t">
                    <div className="text-sm font-medium mb-2">Idioma</div>
                    <div className="flex gap-2">
                      <Link to={location.pathname.replace('/es', '') || '/'} className="px-3 py-1 rounded text-sm bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground" onClick={() => setIsSheetOpen(false)}>
                        Türkçe
                      </Link>
                      <Link to={location.pathname.replace('/es', '/en')} className="px-3 py-1 rounded text-sm bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground" onClick={() => setIsSheetOpen(false)}>
                        English
                      </Link>
                      <span className="px-3 py-1 rounded text-sm bg-primary text-primary-foreground">
                        Español
                      </span>
                    </div>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="space-y-6">
              <Link to={ROUTE_PATHS.HOME_ES} className="flex items-center">
                <div className="bg-white text-primary p-2 rounded">
                  <img src={IMAGES.LOGO_276} alt="Logo" className="h-12 w-auto object-contain" />
                </div>
              </Link>
              <p className="text-sm text-primary-foreground/70 leading-relaxed">
                Desde 1980, hemos estado produciendo soluciones de aluminio de alta calidad en nuestras instalaciones de producción integradas. 
                Aspiramos a la excelencia industrial en cada etapa desde la fundición hasta el procesamiento mecánico.
              </p>
              <div className="flex gap-4">
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiLinkedin className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/mimalum.aluminum" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiInstagram className="h-5 w-5" />
                </a>
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiFacebook className="h-5 w-5" />
                </a>
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiYoutube className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Acceso Rápido</h3>
              <ul className="space-y-4 text-sm text-primary-foreground/70">
                <li><Link to={ROUTE_PATHS.COMPANY_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Empresa</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Instalaciones de Producción</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTS_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Catálogos de Productos</Link></li>
                <li><Link to={ROUTE_PATHS.SUSTAINABILITY_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Sostenibilidad</Link></li>
                <li><Link to={ROUTE_PATHS.DOWNLOAD_CENTER_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Nuestros Certificados</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Unidades de Producción</h3>
              <ul className="space-y-4 text-sm text-primary-foreground/70">
                <li><Link to={ROUTE_PATHS.PRODUCTION_FOUNDRY_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Fundición de Aluminio</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_EXTRUSION_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Extrusión de Perfiles</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_COATING_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Recubrimiento Superficial</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_MECHANICAL_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Procesamiento Mecánico</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTS_INDUSTRIAL_ES} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Soluciones Industriales</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Contáctanos</h3>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-accent shrink-0" />
                  <p className="text-primary-foreground/70">
                    Pelitli Mahallesi Tavşanlı Yolu Caddesi No: 123 <br />
                    Gebze / KOCAELİ
                  </p>
                </div>
                <div className="flex gap-3">
                  <Phone className="h-5 w-5 text-accent shrink-0" />
                  <p className="text-primary-foreground/70">+90 (212) 555 00 00</p>
                </div>
                <div className="flex gap-3">
                  <Mail className="h-5 w-5 text-accent shrink-0" />
                  <p className="text-primary-foreground/70">info@mimalum.com</p>
                </div>
              </div>
              <Button asChild variant="secondary" className="w-full font-bold">
                <Link to={ROUTE_PATHS.CONTACT_ES}>Envíanos un Mensaje</Link>
              </Button>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-primary-foreground/50">
            <p>© 2026 Mimhas Intelligence. Todos los Derechos Reservados.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Política de Privacidad</a>
              <a href="#" className="hover:text-white transition-colors">Términos de Uso</a>
              <a href="#" className="hover:text-white transition-colors">Protección de Datos</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}